import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { avatarOr } from '@/lib/defaultImages';

// ─────────────────────────────────────────────────────────────
// Modération des témoignages marchands (SuperAdmin).
// Les témoignages déposés sur /temoignages arrivent « en attente ».
// Le SuperAdmin peut les APPOUVER (publication sur l'accueil), les
// MASQUER (retirés du site) ou les SUPPRIMER définitivement.
// ─────────────────────────────────────────────────────────────

type Status = 'pending' | 'approved' | 'hidden';

interface TestimonialRow {
  id: number;
  name: string | null;
  role_function: string | null;
  business_name: string | null;
  message: string | null;
  rating: number | null;
  avatar: string | null;
  status: string | null;
  created_at: string | null;
}

type FilterKey = 'pending' | 'approved' | 'hidden' | 'all';

const FILTERS: { key: FilterKey; label: string; icon: string }[] = [
  { key: 'pending', label: 'En attente', icon: 'ri-time-line' },
  { key: 'approved', label: 'Publiés', icon: 'ri-checkbox-circle-line' },
  { key: 'hidden', label: 'Masqués', icon: 'ri-eye-off-line' },
  { key: 'all', label: 'Tous', icon: 'ri-list-check' },
];

function statusMeta(status: string | null): { label: string; className: string; icon: string } {
  switch (status) {
    case 'approved':
      return { label: 'Publié', className: 'bg-accent-100 text-accent-800', icon: 'ri-checkbox-circle-line' };
    case 'hidden':
      return { label: 'Masqué', className: 'bg-red-50 text-red-600', icon: 'ri-eye-off-line' };
    default:
      return { label: 'En attente', className: 'bg-secondary-100 text-secondary-900', icon: 'ri-time-line' };
  }
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

export default function SuperAdminTestimonialsPage() {
  const [rows, setRows] = useState<TestimonialRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey>('pending');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('merchant_testimonials')
        .select('id, name, role_function, business_name, message, rating, avatar, status, created_at')
        .order('created_at', { ascending: false })
        .limit(500);
      if (err) throw err;
      setRows((data || []) as TestimonialRow[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const counts = useMemo(() => ({
    pending: rows.filter((r) => (r.status || 'pending') === 'pending').length,
    approved: rows.filter((r) => r.status === 'approved').length,
    hidden: rows.filter((r) => r.status === 'hidden').length,
    all: rows.length,
  }), [rows]);

  const visible = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => (r.status || 'pending') === filter)),
    [rows, filter],
  );

  const setStatus = useCallback(async (id: number, status: Status) => {
    setBusyId(id);
    try {
      const { error: err } = await supabase
        .from('merchant_testimonials')
        .update({ status })
        .eq('id', id);
      if (err) throw err;
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      setToast(status === 'approved' ? 'Témoignage approuvé et publié.' : status === 'hidden' ? 'Témoignage masqué.' : 'Témoignage remis en attente.');
    } catch {
      setToast("L'opération a échoué. Réessayez.");
    } finally {
      setBusyId(null);
    }
  }, []);

  const remove = useCallback(async (id: number) => {
    setBusyId(id);
    try {
      const { error: err } = await supabase.from('merchant_testimonials').delete().eq('id', id);
      if (err) throw err;
      setRows((prev) => prev.filter((r) => r.id !== id));
      setToast('Témoignage supprimé.');
    } catch {
      setToast('La suppression a échoué. Réessayez.');
    } finally {
      setBusyId(null);
    }
  }, []);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-5">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-chat-quote-line text-primary-600"></i>
          Modération des témoignages
        </h2>
        <p className="text-sm text-foreground-500 mt-1">
          Approuvez, masquez ou supprimez les témoignages marchands avant leur publication sur la page d&apos;accueil.
        </p>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-2 flex-wrap mb-5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              filter === f.key
                ? 'bg-foreground-950 text-background-50'
                : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100'
            }`}
          >
            <i className={f.icon}></i>
            {f.label}
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${filter === f.key ? 'bg-white/25 text-background-50' : 'bg-background-200/70 text-foreground-600'}`}>
              {counts[f.key]}
            </span>
          </button>
        ))}
        <button
          type="button"
          onClick={load}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-foreground-600 border border-background-200/70 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>Rafraîchir
        </button>
      </div>

      {error && (
        <div className="flex flex-col items-center justify-center py-8 mb-6 bg-background-50 border border-background-200/70 rounded-lg">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button
            onClick={load}
            className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Réessayer
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-foreground-400">
          <i className="ri-chat-quote-line text-4xl mb-3"></i>
          <p className="text-sm">Aucun témoignage dans cette catégorie.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
          {visible.map((r) => {
            const sm = statusMeta(r.status);
            const rating = Math.min(5, Math.max(1, Number(r.rating) || 5));
            return (
              <article key={r.id} className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex flex-col">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                    <img src={avatarOr(r.avatar || '')} alt={r.name || 'Marchand'} className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-foreground-950 truncate">{r.name || 'Anonyme'}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${sm.className}`}>
                        <i className={sm.icon}></i>{sm.label}
                      </span>
                    </div>
                    {(r.role_function || r.business_name) && (
                      <div className="text-xs text-foreground-500 truncate">
                        {[r.role_function, r.business_name].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    <div className="flex items-center gap-1 mt-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <i key={s} className={`${s <= rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'} text-xs`}></i>
                      ))}
                      <span className="text-[11px] text-foreground-400 ml-1">{formatDate(r.created_at)}</span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mt-3 mb-4 whitespace-pre-line">{r.message}</p>

                <div className="mt-auto flex items-center gap-2 flex-wrap">
                  {r.status !== 'approved' && (
                    <button
                      type="button"
                      disabled={busyId === r.id}
                      onClick={() => setStatus(r.id, 'approved')}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-accent-600 disabled:opacity-60 transition-colors cursor-pointer"
                    >
                      <i className="ri-check-line"></i>Approuver &amp; publier
                    </button>
                  )}
                  {r.status !== 'hidden' && (
                    <button
                      type="button"
                      disabled={busyId === r.id}
                      onClick={() => setStatus(r.id, 'hidden')}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold whitespace-nowrap hover:bg-background-200/70 disabled:opacity-60 transition-colors cursor-pointer"
                    >
                      <i className="ri-eye-off-line"></i>Masquer
                    </button>
                  )}
                  {r.status !== 'pending' && (
                    <button
                      type="button"
                      disabled={busyId === r.id}
                      onClick={() => setStatus(r.id, 'pending')}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold whitespace-nowrap hover:bg-background-200/70 disabled:opacity-60 transition-colors cursor-pointer"
                    >
                      <i className="ri-time-line"></i>Remettre en attente
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busyId === r.id}
                    onClick={() => remove(r.id)}
                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-red-600 text-xs font-semibold whitespace-nowrap hover:bg-red-50 disabled:opacity-60 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>Supprimer
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] px-4 py-3 rounded-lg border border-background-200/70 bg-background-50 text-sm font-medium text-foreground-800">
          <i className="ri-information-line text-primary-500 mr-2"></i>{toast}
        </div>
      )}
    </div>
  );
}